import React, { useState } from 'react';

import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import DebugModal from '~/components/DebugModal/DebugModal';

import { useQuickDiscount } from '~/hooks/apis/promo/useQuickDiscount';
import useCurrentUser from '~/hooks/useCurrentUser';

import type { QuickDiscountPayload } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';

type Props = { voucherId?: string; brand: QuickDiscountPayload['order']['brand']; disabled: boolean };
type OrderShape = { id: string; label: string; items: QuickDiscountPayload['order']['items'] };
const ORDER_SHAPES: Array<OrderShape> = [
  {
    id: 'hotel',
    label: '$1,000 hotel',
    items: [
      {
        categoryBK: 'hotel',
        subCategoryBK: 'hotel-hotel',
        discountableTotal: 1000,
        numberOfNights: 3,
        numberOfAdults: 2,
      },
    ],
  },
  {
    id: 'hotel-small',
    label: '$100 hotel',
    items: [{ categoryBK: 'hotel', subCategoryBK: 'hotel-hotel', discountableTotal: 100 }],
  },
  {
    id: 'two-hotels',
    label: 'Two $500 hotels',
    items: [
      { categoryBK: 'hotel', subCategoryBK: 'hotel-hotel', discountableTotal: 500 },
      { categoryBK: 'hotel', subCategoryBK: 'hotel-hotel', discountableTotal: 500 },
    ],
  },
  {
    id: 'tour',
    label: '$1,000 tour',
    items: [{ categoryBK: 'tour', subCategoryBK: 'tour-tourbank', discountableTotal: 1000 }],
  },
  {
    id: 'cruise',
    label: '$1,000 cruise',
    items: [{ categoryBK: 'cruise', discountableTotal: 1000 }],
  },
  {
    id: 'experience',
    label: '$100 experience',
    items: [{ categoryBK: 'experience', subCategoryBK: 'experience-led_exp', discountableTotal: 100 }],
  },
  {
    id: 'hotel-experience',
    label: '$1,000 hotel + $100 experience',
    items: [
      {
        categoryBK: 'hotel',
        subCategoryBK: 'hotel-hotel',
        discountableTotal: 1000,
        numberOfNights: 3,
        numberOfAdults: 2,
      },
      { categoryBK: 'experience', subCategoryBK: 'experience-led_exp', discountableTotal: 100 },
    ],
  },
  {
    id: 'hotel-lux-plus',
    label: '$1,000 hotel ($900 LuxPlus price)',
    items: [
      {
        categoryBK: 'hotel',
        subCategoryBK: 'hotel-hotel',
        discountableTotal: 1000,
        luxPlusPrice: 900,
        numberOfNights: 3,
        numberOfAdults: 2,
      },
    ],
  },
];

export default function VoucherQuickTest({ voucherId, brand, disabled }: Props) {
  const { user } = useCurrentUser();
  const [shapeId, setShapeId] = useState(ORDER_SHAPES[0].id);
  const mutation = useQuickDiscount();
  const shape = ORDER_SHAPES.find((candidate) => candidate.id === shapeId) ?? ORDER_SHAPES[0];
  const response = mutation.data;
  const result = response?.result;
  const canRun = Boolean(voucherId && user?.id_member && !disabled);

  return (
    <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">Quick test</Typography>
        <Typography variant="body2" color="text.secondary">
          Preview a synthetic Australian cart in AUD using your admin account. Voucher assignment is bypassed; customer,
          offer and location conditions may need a real order in the promo tester.
        </Typography>
        {!canRun && <Alert severity="info">Save the voucher and any pending changes before testing.</Alert>}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
          <TextField
            select
            label="Order shape"
            value={shapeId}
            disabled={mutation.isPending}
            onChange={(event) => {
              setShapeId(event.target.value);
              mutation.reset();
            }}
            sx={{ minWidth: 240 }}
          >
            {ORDER_SHAPES.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            disabled={!canRun || mutation.isPending}
            onClick={() => {
              if (!canRun || !voucherId || !user?.id_member) {
                return;
              }
              mutation.mutate({
                voucherIds: [voucherId],
                userId: user.id_member,
                requestSource: 'admin',
                ignoreVoucherUserAssignment: true,
                includeFullDetails: true,
                order: {
                  brand,
                  region: 'AU',
                  deviceType: 'web',
                  isGiftOrder: false,
                  items: shape.items.map((item, index) => ({ ...item, transactionKey: `quick-test-${index}` })),
                },
              });
            }}
          >
            {mutation.isPending ? 'Testing…' : 'Preview discount'}
          </Button>
        </Stack>
        {mutation.error && <Alert severity="error">{mutation.error.message}</Alert>}
        {response && !disabled && (
          <Stack spacing={1}>
            {!result ? (
              <Alert severity="error">Discount request failed — no structured result in response.</Alert>
            ) : 'promo' in result ? (
              <Typography variant="body2">
                Voucher discount: {currencyFormatter(result.promo.currency, result.promo.voucherTotal ?? 0, 2)} · Credit
                rewards: {currencyFormatter(result.promo.currency, result.promo.creditTotal ?? 0, 2)}
              </Typography>
            ) : (
              <Alert severity="warning">
                {result.message}
                {result.errors.map((error) => (
                  <Typography key={error.code} variant="body2">
                    {error.code}: {error.message}
                  </Typography>
                ))}
              </Alert>
            )}
            <Box>
              <DebugModal
                type="generic"
                title="Results and debug metadata"
                data={{ request: mutation.variables, response }}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
